import React, { useState } from 'react';
import { Button } from '../ui/button';
import { Eye, EyeOff, ChevronRight, ChevronLeft, Volume2, Sparkles } from 'lucide-react';

// Görsel Kartlar Bileşeni - Resim + Kelime + Çeviri
export default function VisualCards({ title, cards, onAnswer }) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [revealed, setRevealed] = useState(false);
  const [completedCards, setCompletedCards] = useState([]);

  const currentCard = cards[currentIndex];
  const isLastCard = currentIndex === cards.length - 1;
  const allCompleted = completedCards.length === cards.length;

  const handleReveal = () => {
    setRevealed(true);
    if (!completedCards.includes(currentIndex)) {
      setCompletedCards([...completedCards, currentIndex]);
    }
  };

  const handleNext = () => {
    if (isLastCard && allCompleted) {
      if (onAnswer) onAnswer(true, 1);
    } else {
      setCurrentIndex((prev) => Math.min(prev + 1, cards.length - 1));
      setRevealed(false);
    }
  };

  const handlePrev = () => {
    setCurrentIndex((prev) => Math.max(prev - 1, 0));
    setRevealed(false);
  };

  // Ses çalma fonksiyonu (Web Speech API)
  const speakWord = (text) => {
    if ('speechSynthesis' in window) {
      const utterance = new SpeechSynthesisUtterance(text);
      utterance.lang = 'en-US';
      utterance.rate = 0.8;
      window.speechSynthesis.speak(utterance);
    }
  };

  return (
    <div className="w-full max-w-2xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="p-3 bg-purple-100 rounded-xl">
            <Sparkles className="w-6 h-6 text-purple-600" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-gray-800">{title}</h2>
            <p className="text-sm text-gray-500">Görsel Kartlar</p>
          </div>
        </div>
        <div className="text-sm font-medium text-gray-500">
          {currentIndex + 1} / {cards.length}
        </div>
      </div>

      {/* Progress Bar */}
      <div className="w-full bg-gray-200 rounded-full h-2 mb-6">
        <div 
          className="bg-purple-500 h-2 rounded-full transition-all duration-300"
          style={{ width: `${(completedCards.length / cards.length) * 100}%` }}
        />
      </div>

      {/* Card */}
      <div className="bg-white rounded-3xl shadow-xl border border-gray-100 overflow-hidden mb-6">
        {/* Image */}
        {currentCard.image && (
          <div className="relative h-48 bg-gradient-to-br from-blue-50 to-purple-50 flex items-center justify-center">
            <img 
              src={currentCard.image} 
              alt={currentCard.word}
              className="max-h-40 object-contain"
              onError={(e) => {
                e.target.style.display = 'none';
                e.target.parentElement.innerHTML = `<div class="text-6xl">${currentCard.emoji || '📚'}</div>`;
              }}
            />
          </div>
        )}
        
        {/* Emoji fallback if no image */}
        {!currentCard.image && currentCard.emoji && (
          <div className="h-48 bg-gradient-to-br from-blue-50 to-purple-50 flex items-center justify-center">
            <span className="text-8xl">{currentCard.emoji}</span>
          </div>
        )}

        {/* Word Section */}
        <div className="p-6 text-center">
          <div className="flex items-center justify-center gap-3 mb-4">
            <h3 className="text-3xl font-bold text-gray-800">{currentCard.word}</h3>
            <button 
              onClick={() => speakWord(currentCard.word)}
              className="p-2 hover:bg-gray-100 rounded-full transition-colors"
            >
              <Volume2 className="w-6 h-6 text-blue-500" />
            </button>
          </div>
          
          {currentCard.phonetic && (
            <p className="text-gray-400 text-sm mb-4">/{currentCard.phonetic}/</p>
          )}

          {/* Reveal Section */}
          {!revealed ? (
            <button
              onClick={handleReveal}
              className="flex items-center justify-center gap-2 w-full py-4 bg-gradient-to-r from-purple-500 to-blue-500 text-white rounded-xl font-bold text-lg hover:opacity-90 transition-opacity"
            >
              <Eye className="w-5 h-5" />
              Anlamı Göster
            </button>
          ) : (
            <div className="animate-fadeIn">
              <div className="bg-green-50 border-2 border-green-200 rounded-xl p-4 mb-4">
                <p className="text-2xl font-bold text-green-700">{currentCard.translation}</p>
              </div>
              
              {currentCard.example && (
                <div className="bg-blue-50 rounded-xl p-4 text-left">
                  <p className="text-sm text-blue-600 font-medium mb-1">Örnek Cümle:</p>
                  <p className="text-gray-700 italic">"{currentCard.example}"</p>
                  {currentCard.exampleTr && (
                    <p className="text-gray-500 text-sm mt-1">{currentCard.exampleTr}</p>
                  )}
                </div>
              )}

              {currentCard.memoryTip && (
                <div className="mt-4 bg-yellow-50 border border-yellow-200 rounded-xl p-3">
                  <p className="text-sm text-yellow-800">💡 <strong>Hafıza İpucu:</strong> {currentCard.memoryTip}</p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Navigation */}
      <div className="flex gap-3">
        <Button
          onClick={handlePrev}
          disabled={currentIndex === 0}
          variant="outline"
          className="flex-1 py-4 rounded-xl"
        >
          <ChevronLeft className="w-5 h-5 mr-1" />
          Önceki
        </Button>
        <Button
          onClick={handleNext}
          disabled={!revealed}
          className={`flex-1 py-4 rounded-xl ${allCompleted && isLastCard ? 'bg-emerald-500 hover:bg-emerald-600' : 'bg-purple-500 hover:bg-purple-600'}`}
        >
          {allCompleted && isLastCard ? 'Tamamla' : 'Sonraki'}
          <ChevronRight className="w-5 h-5 ml-1" />
        </Button>
      </div>
    </div>
  );
}
